import { Grid } from "@mui/material";
import { useState, useEffect } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { StyledTab, StyledTabs } from "../../styles/HeatReportStyledComponents";
import { CasterProcessData } from "./caster-process-data/CasterProcessData";
import { EAFProcessData } from "./eaf-process-data/EAFProcessData";
import { LabReport } from "./lab-report/LabReport";
import { LMFProcessData } from "./lmf-process-data/LMFProcessData";
import { useHeatReportContext } from "../../context/HeatReportContext";

export const HeatReportTabs: React.FC = () => {
  const { asset } = useParams<{ asset: string }>();
  const [searchParams] = useSearchParams();
  const heatIdFromUrl = searchParams.get("heatId");
  const navigate = useNavigate();
  const [value, setValue] = useState<string | false>(false);

  const {
    state: { treatments, heat },
  } = useHeatReportContext();

  useEffect(() => {
    const buildParams = (processId?: number | null) => {
      const params = new URLSearchParams();
      if (heatIdFromUrl) params.append("heatId", heatIdFromUrl);
      if (processId) params.append("processId", String(processId));
      return params.toString();
    };

    const redirectToEaf = () => {
      setValue("eaf");
      navigate(`/heat-process-report/eaf?${buildParams(heat?.EAFProcessId)}`, {
        replace: true,
      });
    };

    const redirectToLmf = () => {
      setValue("lmf");
      navigate(`/heat-process-report/lmf?${buildParams(heat?.LMFProcessId)}`, {
        replace: true,
      });
    };

    // Wait for heat data to be loaded before redirecting
    if (!heat) return;

    if (asset === "eaf") {
      setValue("eaf");
    } else if (asset?.startsWith("lmf")) {
      if (heat.LMFProcessId === null) {
        redirectToEaf();
        return;
      }
      if (treatments.length > 1) {
        const treatmentNumber = searchParams.get("treatmentNumber");
        if (treatmentNumber) {
          setValue(`lmf-${treatmentNumber}`);
          return;
        }
      }
      setValue(asset);
    } else if (asset === "caster") {
      if (heat.CASProcessId === null) {
        heat.LMFProcessId !== null ? redirectToLmf() : redirectToEaf();
        return;
      }
      setValue("caster");
    } else if (asset === "lab") {
      if (heat.LMFProcessId === null) {
        redirectToEaf();
        return;
      }
      setValue("lab");
    } else {
      setValue("eaf");
      navigate("/heat-process-report/eaf", { replace: true });
    }
  }, [asset, navigate, treatments, searchParams, heat, heatIdFromUrl]);

  const handleChange = (_: React.ChangeEvent<{}>, newValue: string) => {
    setValue(newValue);
    let processId = null;
    let treatmentNumber = undefined;
    if (newValue === "eaf") {
      processId = heat?.EAFProcessId;
    } else if (newValue === "caster") {
      processId = heat?.CASProcessId;
    } else if (newValue === "lab") {
      processId = heat?.LMFProcessId;
    } else if (newValue.startsWith("lmf")) {
      if (treatments.length > 1) {
        const treatmentNum = newValue.split("-")[1];
        const treatment = treatments.find(
          (t) => String(t.TreatmentNumber) === treatmentNum,
        );
        processId = treatment?.ProcessId;
        treatmentNumber = treatment?.TreatmentNumber;
      } else {
        processId = heat?.LMFProcessId;
      }
    }

    const params = new URLSearchParams();
    if (heatIdFromUrl) params.append("heatId", heatIdFromUrl);
    if (processId) params.append("processId", String(processId));
    if (typeof treatmentNumber === "number")
      params.append("treatmentNumber", String(treatmentNumber));
    navigate(`/heat-process-report/${newValue}?${params.toString()}`);
  };

  if (value === false) {
    return null;
  }

  return (
    <>
      <StyledTabs value={value} onChange={handleChange}>
        <StyledTab label="EAF" value="eaf" />
        {treatments.length > 1 ? (
          treatments.map((treatment) => (
            <StyledTab
              key={`lmf-tab-${treatment.TreatmentNumber}`}
              label={`LMF ${treatment.TreatmentNumber}`}
              value={`lmf-${treatment.TreatmentNumber}`}
            />
          ))
        ) : (
          <StyledTab
            label="LMF"
            value="lmf"
            disabled={heat.LMFProcessId === null}
          />
        )}
        <StyledTab
          label="Caster"
          value="caster"
          disabled={heat.CASProcessId === null}
        />
        <StyledTab
          label="Lab"
          value="lab"
          disabled={heat.LMFProcessId === null}
        />
      </StyledTabs>

      <Grid item xs={12} container spacing={1}>
        {asset === "eaf" && <EAFProcessData />}
        {asset?.startsWith("lmf") && <LMFProcessData />}
        {asset === "caster" && <CasterProcessData />}
        {asset === "lab" && <LabReport />}
      </Grid>
    </>
  );
};
